import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { usePersistedState } from '../lib/persistedState.ts';
import { useBuildForSlug } from '../lib/useBuilds.ts';
import { useMediaQuery, PHONE_QUERY } from '../lib/useMediaQuery.ts';
import { slideScope, deckScope, selectionScope } from '../lib/scope.ts';
import { deckScopeHandback } from '../lib/chatScope.ts';
import { useChatStream } from '../lib/useChatStream.ts';
import { useDeckActivity, notifyActivityChanged } from '../lib/useDeckActivity.ts';
import { canPreviewRow, canPreviewCreateRow, previewRow, previewCreateRow } from '../lib/proposalActions.ts';
import type { SlideRow } from '../lib/proposalActions.ts';
import { useBusySlide, isBusyOn, releaseBusy } from '../lib/userBusy.ts';
import ProposalBanner from '../components/ProposalBanner.tsx';
import { DECK_TOOL_NAMES, SLIDE_TOOL_NAMES } from '../lib/tools.ts';
import { getSlice, useTurnSlice, stopTurn, reloadDeckThreads } from '../lib/chatTurns.ts';
import { actionPrompt, overflowFixPrompt } from '../lib/actionNotes.ts';
import { geometryAsk } from '../lib/geometryAsk.ts';
import { useGeometryBadgeFix } from '../lib/useGeometryBadgeFix.ts';
import { assetAsk, assetRefs } from '../lib/assetAsk.ts';
import { useDeckFindings } from '../lib/useDeckFindings.ts';
import { useModelChoice } from '../lib/useModelChoice.ts';
import { STRUCTURAL_MODEL, resolveModelChoice } from '../lib/models.ts';
import { useDeckEditor } from '../editor/useDeckEditor.ts';
import { baseOf } from '../lib/slideBlocks.ts';
import Filmstrip from '../canvas/Filmstrip.tsx';
import SectionModal from '../canvas/SectionModal.tsx';
import { useSectionBuild } from '../editor/useSectionBuild.ts';
import AddSlideChooser from '../canvas/AddSlideChooser.tsx';
import DescribeSlideModal from '../canvas/DescribeSlideModal.tsx';
import ChangeTemplateChooser from '../canvas/ChangeTemplateChooser.tsx';
import PreviewFrame from '../components/PreviewFrame.tsx';
import CommandBar from '../canvas/CommandBar.tsx';
import DeckOverview from '../canvas/DeckOverview.tsx';
import AssetsModal, { type ResolvedAsset } from '../canvas/AssetsModal.tsx';
import DeckOptionsDrawer from '../canvas/DeckOptionsDrawer.tsx';
import PublishDrawer from '../canvas/PublishDrawer.tsx';
import SettingsDrawer from '../canvas/SettingsDrawer.tsx';
import ThemeToggle from '../components/ThemeToggle.tsx';
import BuildsIndicator from '../components/BuildsIndicator.tsx';
import Toast from '../components/Toast.tsx';
import SaveToLibraryModal from '../components/SaveToLibraryModal.tsx';
import { ChevronLeft, Settings } from '../lib/icons.tsx';
import AppHeader from '@shared-client/AppHeader.tsx';
import type { AutoSend, CommandBarProps } from '../canvas/CommandBar.tsx';
import type { JumpTarget, ActionNotice, RunningRow } from '../canvas/ActivityPanel.tsx';
import type { MergeRequest } from '../canvas/DeckOverview.tsx';
import type { SlideActivity } from '../canvas/Filmstrip.tsx';
import type { ActivityEntry, ToolCall } from '../lib/chatTurns.ts';
import type {
  ActivityRow,
  DeckActivityPayload,
  DeckApplyAllPayload,
  LibrarySavePayload,
  ValidationPayload,
} from '../../shared/api.ts';
import type { SaveToLibraryPayload } from '../components/SaveToLibraryModal.tsx';
import type { TemplateChoice, TemplateRun } from '../canvas/ChangeTemplateChooser.tsx';
import type { SlideEntry } from '../../shared/deck.ts';
import type { AddChooser } from '../editor/useDeckEditor.ts';

/** Which side drawer is open. */
type Drawer = 'assets' | 'publish' | 'settings' | 'options' | null;

/**
 * The whole-deck overview, when open. `mode: 'merge'` opens it straight into
 * slide-picking from a slide's ⋮ menu; `seedSlideId` pre-selects that slide.
 */
type Overview = { mode?: 'merge' | null; seedSlideId?: string } | null;

// The bank (shelf) deck slugs — mirrors the server's pipeline/banks.ts BANKS.
// Saving FROM a bank is meaningless, so these decks don't get the menu item.
const BANK_SLUGS = new Set(['rkh-library']);

/**
 * The one screen: filmstrip + live preview + a chat command bar that expands
 * into the conversation. Generative work is "tell the AI, watch the deck
 * change"; deterministic structure is direct manipulation — drag to reorder,
 * kebab menus for duplicate/delete/move, section rename/recolour in the
 * filmstrip. Both paths run through the same server codecs and the same
 * refresh pipeline (refreshSummary / bumpPreviewReload).
 *
 * The structural machinery all lives in editor/useDeckEditor.js — the SAME
 * core the library item editor runs — so the two surfaces can't drift. What
 * stays here is deck-editor-specific: chat scope (automatic, visible,
 * clearable — a focused slide chats in slide scope, clearing the chip talks
 * to the whole deck), the describe-it-in-chat add path, drawers, publish,
 * and save-to-library.
 */
export default function Canvas() {
  const location = useLocation();
  const navigate = useNavigate();
  const slug = useMemo(() => location.pathname.replace(/^\/editor\//, '').replace(/\/$/, ''), [location.pathname]);

  // A deck under active build has no business being edited: chat and the codecs
  // write the same slide files the build subprocess is writing, and nothing
  // serialises a UI write against `claude -p` (docs/deck-structure.md — the
  // recorded answer is to refuse the write, not to reach for a bigger lock).
  // Publishing one would ship a half-written deck. So hand over to the build
  // watcher, which already has the filmstrip, the live preview and the
  // transcript, and already returns here when the build finishes.
  //
  // `undefined` means the first builds poll has not landed; redirecting on that
  // would flash the editor open, so only a KNOWN active build redirects.
  const activeBuild = useBuildForSlug(slug);
  useEffect(() => {
    if (activeBuild) navigate('/build/' + activeBuild.id, { replace: true, state: { slug } });
  }, [activeBuild, navigate, slug]);

  // Stamp the deck as opened, for the dashboard's "Jump back in" strip, the
  // Recently opened row and the Last opened sort. Fire-and-forget: this is
  // bookkeeping, and a deck that fails to record an open should still open.
  // The route ignores a stamp under a minute old, so dev hot-reload remounting
  // this component does not rewrite studio.json on a loop.
  useEffect(() => {
    if (!slug || activeBuild) return;
    void api.post(`/api/decks/${slug}/opened`).catch(() => {});
  }, [slug, activeBuild]);

  // True after the user ✕'s the chip — deck scope. Sticky while its work is in
  // flight, then released on the next slide change (see the hand-back effect).
  const [scopeCleared, setScopeCleared] = useState(false);
  // Which slide was focused when deck scope was engaged, so the hand-back can
  // tell "the user has moved on" from "the handoff jumped us here".
  const deckScopeAnchor = useRef<string | null>(null);
  // Filmstrip multi-selection (block baseIds) — lifted here so it can become
  // the chat scope ("N slides") and the programmatic chat handoffs
  // (merge/split/change-template) can clear it before they fire.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  // A live selection IS the chat scope. The original design gated this behind
  // the "Edit with AI" click (so drag-reorder selections didn't move the
  // scope), but that made the natural flow silently wrong: select slides, type
  // a prompt → it went to the WHOLE DECK (Jake hit this live, 2026-07-29).
  // The scope chip shows "N slides" and clears in one click, so the visible
  // switch is the lesser surprise. selectionArmed now only tracks whether the
  // floating "Edit with AI" bar has been used (it hides after the click).
  const [selectionArmed, setSelectionArmed] = useState(false);
  useEffect(() => {
    if (selectedIds.size === 0) setSelectionArmed(false);
  }, [selectedIds]);
  const [drawer, setDrawer] = useState<Drawer>(null);
  // `?publish=1` opens the publish drawer on arrival — the dashboard's card
  // menu offers Publish, and publishing lives here rather than being a second
  // implementation over there. The param is consumed once so a reload or a
  // back-navigation does not re-open it.
  const [publishParam, setPublishParam] = useSearchParams();
  useEffect(() => {
    if (publishParam.get('publish') !== '1') return;
    setDrawer('publish');
    const next = new URLSearchParams(publishParam);
    next.delete('publish');
    setPublishParam(next, { replace: true });
  }, [publishParam, setPublishParam]);
  // Whole-deck thumbnail grid: null when closed, else { mode, seedSlideId } —
  // mode 'merge' opens it straight into slide-picking from a slide's ⋮ menu.
  const [overview, setOverview] = useState<Overview>(null);
  // The conversation's open state is TWO states wearing one name.
  //
  // On a desktop it is a remembered preference. On a phone the panel covers
  // the slide it is talking about, so it has to start closed every time — and
  // it cannot share the persisted key, or a desktop session left open would
  // hand the phone an open panel on first load. The phone's copy is therefore
  // session-only, and the desktop key is never written from a phone.
  const isPhone = useMediaQuery(PHONE_QUERY);
  const [deskConvOpen, setDeskConvOpen] = usePersistedState('studio.convOpen', false);
  const [phoneConvOpen, setPhoneConvOpen] = useState(false);
  const convOpen = isPhone ? phoneConvOpen : deskConvOpen;
  // Every caller goes through this, so neither copy can be written directly
  // and the two can't drift.
  const setConvOpen = useCallback(
    (v: boolean) => (isPhone ? setPhoneConvOpen(v) : setDeskConvOpen(v)),
    [isPhone, setDeskConvOpen],
  );
  // Seeds the command bar input once. A bare string REPLACES the box; the
  // object form joins what is already typed (the assets room's "Add to chat").
  const [prefill, setPrefill] = useState<string | { text: string; mode: 'append' } | null>(null);
  // The describe-a-slide room, opened OVER the add-slide gallery. Same shape as
  // addChooser because it inherits that chooser's position.
  const [describeSlide, setDescribeSlide] = useState<AddChooser>(null);
  // One-shot programmatic chat send { prompt, slideId, model } — fires once the
  // scope has caught up to slideId (Change-template's AI turn). See CommandBar
  // autoSend. Every site here names its `model`: a turn the STUDIO fires must
  // not inherit whatever the chat pill was left on.
  const [autoSend, setAutoSend] = useState<AutoSend | null>(null);
  // What the server can run a turn on, plus the ⚙ "Default model" — shared with
  // the command bar's pill (lib/useModelChoice.js).
  const { defaultChoice, options: modelOptions } = useModelChoice();
  // Merge and split delete/create slides, so they start on the strongest model
  // rather than the ⚙ default (see STRUCTURAL_MODEL). resolveModelChoice keeps
  // that honest on a server with no Claude credential, where Opus isn't on offer.
  const structuralModel = resolveModelChoice(modelOptions, STRUCTURAL_MODEL);
  // One-shot Chat-tab force: "Edit with AI" must land the user in the chat
  // input even if the panel was on the Activity/Edit tab (the click read as
  // "nothing happened" otherwise).
  const [forceChatTab, setForceChatTab] = useState(false);
  // Stop/leftover toasts offer a "View" that must land on Activity — that's
  // where the kept proposals and any revertible leftovers are.
  const [forceActivityTab, setForceActivityTab] = useState(false);
  // Target slide for the Change-template chooser | null.
  const [changeTemplate, setChangeTemplate] = useState<SlideEntry | null>(null);
  // The change-template AI turn, watched in the background (the chooser closes
  // as soon as it is fired) so every outcome reaches the user as a toast rather
  // than only as a line in the chat.
  //   { key, slideId, detail, phase, message, startedAt }
  //   phase: 'starting' (sent, not seen streaming yet) | 'running' | 'failed'
  //   'failed' is PRE-FLIGHT only — a refusal before anything ran, shown in the
  //   still-open chooser. Everything after the send is toasted and cleared.
  // `key` is captured ONCE at confirm: activeSlide moves under us (jumpToSlide,
  // the review queue), so re-deriving the scope would watch the wrong turn.
  const [templateRun, setTemplateRun] = useState<TemplateRun | null>(null);
  // Slides pending a "Save to library" (the category-pick modal) | null. An
  // array: one slide from a filmstrip ⋮, a selection from the overview.
  const [saveToLibrary, setSaveToLibrary] = useState<SlideEntry[] | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const ed = useDeckEditor({
    slug,
    // A deliberate focus-to-edit (empty-variant insert) re-engages slide scope
    // immediately. Ordinary navigation doesn't fire this — it goes through the
    // hand-back effect below, which waits for the deck work to settle first.
    onUserSelect: () => setScopeCleared(false),
  });
  const { summary, slides, activeIdx, activeSlide, sectionName, mutate, addChooser, setAddChooser } = ed;
  // The badge-click listener is mounted once (it must not re-subscribe on every
  // deck change), so it reads the current slides through a ref rather than
  // closing over a stale array.
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  // New Section modal + its build lifecycle (create-empty or plan-then-build a
  // section from uploaded content). Slides stream into the filmstrip as they
  // land; the modal shows the plan-review gate during planning/review.
  const sectionBuild = useSectionBuild({ slug, refreshSummary: ed.refreshSummary });
  // A failed section build closes the modal with a toast (the empty section it
  // may have created stays — it's editable).
  useEffect(() => {
    if (sectionBuild.phase === 'failed') {
      // tone:'error' — a multi-minute build that failed, possibly leaving a
      // half-written section behind, announced itself with the same neutral
      // chrome and 3.5s life as "Added a slide", then vanished. Every other
      // failure path in the app already passes the error tone (6s); this was
      // the outlier.
      ed.toast.pushToast(sectionBuild.error || 'The section build stopped before finishing.', { tone: 'error' });
      sectionBuild.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionBuild.phase]);

  // ----- chat --------------------------------------------------------------

  // One scope object drives the endpoint + apply contract under the hood.
  // A filmstrip multi-selection wins: one prompt applied to all selected
  // slides in a single constrained deck turn (per-slide review cards).
  const scope = useMemo(() => {
    if (selectedIds.size > 0) {
      const seen = new Set<string>();
      const picked: SlideEntry[] = [];
      for (const s of slides || []) {
        const base = baseOf(s.id);
        if (!selectedIds.has(base) || seen.has(base)) continue;
        seen.add(base);
        picked.push({ ...s, id: base });
      }
      if (picked.length > 0) return selectionScope(slug, picked);
    }
    if (activeSlide && !scopeCleared) return slideScope(slug, activeSlide);
    return deckScope(slug);
    // Keyed on activeSlide?.id, not the object: every summary poll hands back a
    // new object, and re-scoping on that would restart the chat stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, activeSlide?.id, scopeCleared, selectedIds, slides]);

  const chat = useChatStream(scope);
  // Durable AI-change rows (Activity tab + filmstrip ready-dots). One fetch
  // cycle shared through this hook; actions notify via notifyActivityChanged.
  const activity = useDeckActivity(slug);
  // The slide the user is editing right now, or null (lib/userBusy.js). Both
  // things this screen does the instant a proposal lands — preview it onto the
  // real file, jump the canvas to it — are held while this is set.
  const busySlideId = useBusySlide();

  // Hand the chat back to slide scope once whole-deck work is DONE — the rule
  // and the reasoning live in lib/chatScope.js (pure, unit-tested).
  // 'ready' is the status of a proposal awaiting review — there has never
  // been a 'proposed' one, which is what this used to test, so the second half
  // of the rule was dead and deck scope was released the moment the RUN ended
  // rather than when its cards were settled.
  const deckWorkLive = (activity.running || []).length > 0 || (activity.rows || []).some((r) => r.status === 'ready');
  useEffect(() => {
    const action = deckScopeHandback({
      scopeCleared,
      anchor: deckScopeAnchor.current,
      activeSlideId: activeSlide?.id ?? '',
      deckWorkLive,
    });
    if (action === 'clear') deckScopeAnchor.current = null;
    else if (action === 'anchor') deckScopeAnchor.current = activeSlide?.id ?? '';
    else if (action === 'release') setScopeCleared(false);
  }, [scopeCleared, activeSlide?.id, deckWorkLive]);

  // Deck-wide layout findings for the Activity tab. The same pass that captures
  // each slide measures its geometry, so this is what makes a broken slide 31
  // visible without scrolling to it (Jake, 2026-08-02) — the live checks only
  // ever see the slide in the preview.
  //
  // It stays WHOLE-DECK: a viewport-gated measurement would under-report, and a
  // panel that quietly lists fewer problems than exist is worse than a slow one.
  // But it no longer runs on mere editor open, which was rendering every slide
  // in the deck for a tab most sessions never look at. Until Activity is opened
  // this is a cold read of what has already been measured; opening it latches
  // `warmFindings` on and the full pass starts.
  //
  // Latched rather than tracking the tab, because leaving Activity mid-render
  // must not abandon the pass that the panel is counting down.
  const [warmFindings, setWarmFindings] = useState(false);
  useEffect(() => setWarmFindings(false), [slug]);
  const findings = useDeckFindings(slug, ed.status === 'ready', warmFindings);

  // slide id -> 'streaming' | 'unread', for the filmstrip activity dots.
  // Live turns win; slides with an unreviewed proposal or live preview from the
  // durable history also dot as 'unread' so completed work is findable after a
  // reload, not just in the session it streamed in.
  const slideActivity = useMemo(() => {
    const out: Record<string, SlideActivity> = {};
    for (const row of activity.rows || []) {
      if (row.slideId && (row.status === 'ready' || row.previewing)) out[row.slideId] = 'unread';
    }
    for (const v of Object.values(chat.activity || {})) {
      // A selection turn targets N slides — every one gets the dot. Selection
      // ids are base (file-level), so fan them out to every instance of a
      // multi: N block (whose tiles key on the full "base#n" id). Mid-run,
      // slides the agent has already written flip to 'done' (solid dot) so
      // the wait reads as progress, not a stall.
      for (const id of v.slideIds || (v.slideId ? [v.slideId] : [])) {
        // A cleared turn reports `null`, which is not a dot state — skip it
        // rather than writing an entry the filmstrip has to defend against.
        const state: SlideActivity | null =
          v.state === 'streaming' && v.progress?.doneIds?.includes(id) ? 'done' : v.state;
        if (!state) continue;
        out[id] = state;
        for (const s of slides || []) {
          if (s.id !== id && baseOf(s.id) === id) out[s.id] = state;
        }
      }
    }
    return out;
  }, [chat.activity, activity.rows, slides]);

  // Proposals show themselves: the newest previewable ready proposal per slide
  // auto-previews the moment it lands, so the deck always shows the updated
  // design — the banner/row flip is how you get the original back. Each card
  // is attempted once (a discarded preview must not resurrect itself).
  // ...unless the user has their hands on a slide. A preview REWRITES the real
  // slide file, so showing itself means overwriting what someone is editing
  // (lib/userBusy.js). Held proposals aren't lost — they're already `ready`
  // rows, so the review bar counts them and the filmstrip tags their slide —
  // and `busySlideId` is in the deps, so the hold releases itself the moment
  // focus leaves the Edit panel. Crucially the card is NOT marked attempted
  // while held, or it would never preview at all.
  const autoPreviewedRef = useRef(new Set<string>());
  useEffect(() => {
    const seenSlide = new Set();
    for (const row of activity.rows || []) {
      if (row.status !== 'ready') continue;
      // A proposed NEW slide has no file to overwrite: it previews by being
      // inserted into the deck, marked New until it's kept or discarded. Do
      // that here too, so "5 proposals awaiting review" means five slides you
      // can actually walk through, not three phantoms the walk skips over.
      if (canPreviewCreateRow(row)) {
        const key = row.toolId;
        if (!key || autoPreviewedRef.current.has(key)) continue;
        // An insert shifts every index below it, and useDeckEditor clamps
        // activeIdx rather than re-resolving the active slide by id — so a
        // slide added ABOVE the user swaps what they're editing with no
        // navigation event. Wait for ANY edit to finish, not just this slide's.
        if (busySlideId) continue;
        autoPreviewedRef.current.add(key);
        previewCreateRow(slug, row)
          .then(() => {
            ed.refreshSummary();
            ed.bumpPreviewReload();
          })
          .catch(() => {});
        continue;
      }
      const slideId = row.slideId;
      if (!slideId || seenSlide.has(slideId)) continue;
      seenSlide.add(slideId); // only the newest row per slide is a candidate
      if (row.previewing || !canPreviewRow(row)) continue;
      const key = row.toolId || row.historyId;
      if (!key || autoPreviewedRef.current.has(key)) continue;
      // An edit only disturbs the one slide it overwrites, so it waits only
      // while THAT slide is being edited — a proposal for slide 7 lands
      // freely while the user works on slide 3.
      if (isBusyOn(slideId)) continue;
      autoPreviewedRef.current.add(key);
      // Naming the id is what lets the checker see the guard above; `previewRow`
      // takes a row that HAS one, which is the whole point of the check.
      previewRow(slug, { ...row, slideId })
        .then(() => ed.bumpPreviewReload())
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.rows, slug, busySlideId]);

  // Slides sitting in the deck as unreviewed proposals — the filmstrip tags
  // them New so a slide you haven't accepted never reads as part of the deck.
  const pendingSlideIds = useMemo(() => {
    const out = new Set<string>();
    for (const row of activity.rows || []) if (row.pending && row.slideId) out.add(row.slideId);
    return out;
  }, [activity.rows]);

  // Slides whose validation BLOCKS publishing (bundled demo logos, a team on
  // colleagues' portraits). Read here as well as in the Publish drawer so the
  // filmstrip can say WHERE while you are still editing — finding out at publish
  // time is finding out too late. Re-reads on the same counter the preview uses,
  // so fixing a slide clears its tag.
  const [blockedSlideIds, setBlockedSlideIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    let live = true;
    api
      .get<ValidationPayload>(`/api/build/${slug}/validation`)
      .then((v) => {
        if (!live) return;
        setBlockedSlideIds(new Set((v.issues || []).filter((i) => i.severity === 'error').map((i) => i.slide)));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [slug, ed.previewReloadKey]);

  // The review under the user's eyes: the newest live-review row for the
  // focused slide drives the on-slide banner (proposal / revert / re-apply).
  const activeReviewRow = useMemo<SlideRow | null>(() => {
    const focused = activeSlide;
    if (!focused) return null;
    const hit = (activity.rows || []).find((r) => r.slideId === focused.id && r.previewing && r.status !== 'rejected');
    // Matched ON `focused.id`, so its slideId is that string — the narrowed
    // shape is what ProposalBanner and every preview verb take.
    return hit ? { ...hit, slideId: focused.id } : null;
    // Keyed on the id — only activeSlide.id is read here. See the scope memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.rows, activeSlide?.id]);

  // A turn is still working on the focused slide (typically a preview
  // self-check round-trip fixing its own proposal). Reviewing NOW would judge
  // a version the model is about to replace — and Keep/Discard racing the
  // incoming card left the review "stuck" (Jake, 2026-07-31). The banner
  // waits it out; the streaming dot state already tracks exactly this.
  const activeSlideWorking = Boolean(activeSlide) && slideActivity[activeSlide?.id ?? ''] === 'streaming';

  // Every proposal awaiting review, newest per slide, in deck order — the
  // banner's stepper walks this queue so a batch of N proposals (a selection
  // turn) reviews as "2 of 5 · next", not a filmstrip scavenger hunt.
  const reviewQueue = useMemo(() => {
    const bySlide = new Map<string, ActivityRow>();
    for (const row of activity.rows || []) {
      if (!row.slideId || row.status !== 'ready') continue;
      if (!bySlide.has(row.slideId)) bySlide.set(row.slideId, row); // rows are newest-first
    }
    const orderOf = (id: string | null) => (slides || []).findIndex((s) => s.id === id || baseOf(s.id) === id);
    // Only slides the walk can actually land on. A create card is in the deck
    // (pending) by the time it queues; one whose insert failed validation isn't
    // in the filmstrip at all, and queueing it made Review a dead click.
    return [...bySlide.values()]
      .filter((r) => orderOf(r.slideId) >= 0)
      .sort((a, b) => orderOf(a.slideId) - orderOf(b.slideId));
  }, [activity.rows, slides]);

  // "slide 04 “The third crash”" — a proposal that no longer moves the canvas
  // has to say WHERE it landed, or "ready to review" is a riddle.
  const slideLabelFor = (slideId: string | null) => {
    const i = (slides || []).findIndex((s) => s.id === slideId || baseOf(s.id) === slideId);
    const s = slides[i];
    if (i < 0 || !s) return '';
    return `slide ${String(i + 1).padStart(2, '0')}${s.title ? ` “${s.title}”` : ''}`;
  };

  // Slides carrying an AI EDIT the user hasn't decided on. A proposed new slide
  // already says "New", which is the stronger statement, so it's excluded here.
  // Edits had no filmstrip mark at all — they didn't need one while a proposal
  // always previewed itself onto the slide the moment it landed. Now that a
  // proposal can be held (lib/userBusy.js), the deck has to say where it went.
  // Fanned out to every instance of a multi:N block, as slideActivity does:
  // row ids are file-level, tile ids are "base#n".
  const reviewSlideIds = useMemo(() => {
    const out = new Set<string>();
    for (const r of reviewQueue) {
      if (!r.slideId || pendingSlideIds.has(r.slideId)) continue;
      out.add(r.slideId);
      for (const s of slides || []) if (baseOf(s.id) === r.slideId) out.add(s.id);
    }
    return out;
  }, [reviewQueue, pendingSlideIds, slides]);

  // A finished proposal ANNOUNCES itself. It never moves you (Jake,
  // 2026-08-01).
  //
  // This used to walk the canvas to the first fresh proposal, on the reasoning
  // that the review banner is meaningless if the canvas is elsewhere. But a
  // turn finishes when it finishes, and the user is by then reading a different
  // slide, or writing one: an edit lands somewhere else in the deck and the
  // screen changes under them. The banner is not meaningless while they stay
  // put — it is a standing "N proposals awaiting review" with a Review button
  // that starts the walk on demand, which is the same walk, at a moment they
  // chose. So: a toast that names the slide and offers to go there, and the
  // banner keeps the work until they do.
  //
  // The old code carried two exceptions (don't jump if they're already on a
  // queued slide, don't jump while `busySlideId` holds a focused field). Both
  // are gone with the jump itself — nothing here navigates any more, so there
  // is nothing to make an exception to.
  const seenQueueRef = useRef(new Set<string>());
  useEffect(() => {
    const ids = reviewQueue.map((r) => r.toolId).filter((id): id is string => !!id);
    const fresh = ids.filter((id) => !seenQueueRef.current.has(id));
    ids.forEach((id) => seenQueueRef.current.add(id));
    if (fresh.length === 0) return;
    // Silent when they are already looking at the slide it landed on: the
    // proposal banner is right there saying the same thing.
    const base = activeSlide ? baseOf(activeSlide.id) : null;
    const first = reviewQueue[0];
    if (!first) return;
    if (base && reviewQueue.some((r) => r.slideId === activeSlide?.id || r.slideId === base)) return;
    const where = slideLabelFor(first.slideId);
    ed.toast.pushToast(
      fresh.length > 1
        ? `${fresh.length} proposals ready to review.`
        : `Proposal ready to review${where ? ` on ${where}` : ''}.`,
      { actionLabel: 'Review', undo: async () => first.slideId && ed.jumpToSlide(first.slideId) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewQueue]);

  // Clicking a red geometry badge writes the fix into the chat composer, ready
  // to send. The badge says "click to fix", so it has to put a fix within one
  // click — it used to open the Edit tab and stop there, which (because the
  // panel had no finding to show, see EditPanel's request effect) read as a
  // button that did nothing at all (Jake, 2026-08-02).
  //
  // It PREFILLS rather than sending: the user sees exactly what is about to be
  // asked and can edit it or think better of it. "Looks right" — the other real
  // exit — stays one tab away in Edit, where the waiver switch lives.
  //
  // The finding travels ON the click (`kind` + `detail`) because the studio has
  // no way to reconstruct it: the checks report on a transition, long since
  // passed by the time anyone clicks.
  useGeometryBadgeFix(({ idx, kind, detail }) => {
    // Slide scope: this is a fix for ONE slide, and deck scope would aim it at
    // the whole deck. Focus the slide the badge is on, too — a badge can only
    // be clicked on a visible slide, but the chat's scope follows the FOCUSED
    // slide, which may still be its neighbour after a scroll.
    setSelectedIds(new Set());
    setScopeCleared(false);
    setConvOpen(true);
    const target = idx >= 0 ? slidesRef.current?.[idx] : null;
    if (target) ed.jumpToSlide(target.id);
    setPrefill(geometryAsk({ kind, detail }));
  });

  // Alert on completion: a turn that finishes while its scope is off-screen
  // flags 'unread' — surface a toast with a View action so the user knows
  // without watching the filmstrip dots or the Activity tab.
  const prevActivityRef = useRef<Record<string, ActivityEntry>>({});
  useEffect(() => {
    const prev = prevActivityRef.current;
    for (const [key, v] of Object.entries(chat.activity || {})) {
      if (v.state === 'unread' && prev[key]?.state !== 'unread') {
        const label = v.label || (v.kind === 'deck' ? 'Whole deck' : 'a slide');
        ed.toast.pushToast(`AI finished on “${label}”: review the proposal`, {
          actionLabel: 'View',
          undo: async () => {
            releaseBusy(); // the user asked to see it — let any held preview through
            handleJumpToTurn({ kind: v.kind, slideId: v.slideId });
          },
        });
      }
    }
    prevActivityRef.current = chat.activity || {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.activity]);

  // Stopping an AI edit. The server kills the run, contains its side effects
  // and keeps whatever slides were already finished as reviewable proposals —
  // so the honest message is "here's what it got through", not silence. A run
  // that had already finished reports that instead of a phantom cancel.
  async function handleStopRun(t: RunningRow) {
    const before = (activity.rows || []).filter((r) => r.status === 'ready').length;
    let stopped = false;
    try {
      ({ stopped } = await stopTurn(t.key, t.stopTarget));
    } catch {
      /* the toast below still tells the user where to look */
    }
    if (!stopped) {
      ed.toast.pushToast('That edit had already finished: see the Activity tab.', {
        actionLabel: 'View',
        undo: async () => setForceActivityTab(true),
      });
      return;
    }
    // Containment + persistence take a moment, so poll the server for the
    // outcome rather than reading our own (pre-stop) rows — counting those
    // would always report nothing kept.
    const deadline = Date.now() + 45_000;
    let settled: { kept: number; leftovers: number } | null = null;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const res = await api.get<Partial<DeckActivityPayload>>(`/api/chat/${slug}/history`);
        const rows = res?.rows || [];
        const stillRunning = (res?.running || []).some(
          (r) => r.threadKey === (t.stopTarget?.threadKey ?? (t.slideId ? baseOf(t.slideId) : '_deck')),
        );
        if (stillRunning) continue;
        settled = {
          kept: Math.max(0, rows.filter((r) => r.status === 'ready').length - before),
          leftovers: rows.filter((r) => r.leftover && r.status === 'applied').length,
        };
        break;
      } catch {
        /* transient — keep waiting */
      }
    }
    notifyActivityChanged();
    if (!settled) return; // the panel still updates; don't claim an outcome we never saw
    const parts: string[] = [];
    parts.push(
      settled.kept > 0
        ? `Stopped. ${settled.kept} finished slide${settled.kept === 1 ? '' : 's'} kept for review.`
        : 'Stopped. The deck was put back as it was.',
    );
    if (settled.leftovers > 0) {
      parts.push(`${settled.leftovers} slide${settled.leftovers === 1 ? '' : 's'} left changed: revert from Activity.`);
    }
    ed.toast.pushToast(parts.join(' '), {
      tone: settled.leftovers > 0 ? 'error' : 'info',
      actionLabel: 'View',
      undo: async () => setForceActivityTab(true),
    });
  }

  // Tool cards report back through onApplied: slide tools just need an
  // iframe reload; deck tools mutate the manifest, so refetch the summary
  // and (for create_slide) jump to the new slide for follow-up iteration.
  function handleApplied(tool: ToolCall, res: unknown) {
    const deckTool = DECK_TOOL_NAMES.has(tool?.name);
    // The tool's own result shape varies by tool, and only create_slide's
    // `slideId` is read here — so it is narrowed at the one place that wants it.
    const slideId = res && typeof res === 'object' && 'slideId' in res ? String(res.slideId) : null;
    if (deckTool && res) {
      ed.refreshSummary();
      if (tool.name === 'create_slide' && slideId) ed.setPendingNewSlideId(slideId);
    } else {
      ed.bumpPreviewReload();
    }
    notifyActivityChanged();
  }

  // An Activity-tab action (apply/reject/revert/discard) landed on disk —
  // refresh everything; cheap, and covers slide + structural changes alike.
  //
  // reloadThread is the load-bearing one: the same proposal is ALSO a card in
  // the conversation, and until this was added the chat pane never re-read the
  // thread after an Activity action. It relied on an optimistic in-memory patch
  // that silently misses whenever the row has no toolId, the scope isn't loaded,
  // or the key doesn't match — leaving the card offering Apply for something
  // already applied and reverted (Jake, 2026-07-30). Apply-all/Reject-all always
  // reloaded for exactly this reason; the Activity path simply never did.
  function handleActivityChanged(notice?: ActionNotice) {
    ed.refreshSummary();
    ed.bumpPreviewReload();
    reloadDeckThreads(slug);
    // A revert/re-apply moves its row out of "Awaiting review" and down into
    // "Changes" — correct, but from the user's seat the row just disappears.
    // Name what happened and offer the way back to it.
    if (notice?.kind === 'revert' || notice?.kind === 'reapply') {
      const what = notice.title ? `“${notice.title}”` : 'that change';
      ed.toast.pushToast(notice.kind === 'revert' ? `Reverted: ${what} put back.` : `Re-applied: ${what} restored.`, {
        actionLabel: 'View',
        undo: async () => setForceActivityTab(true),
      });
    }
  }

  // Whole-deck "Apply all": hand the turn's pending deck cards to the server,
  // which applies them in dependency order (sections before their slides, a
  // slide anchored to another new slide after it, reorders last) so an insert
  // can't break the next card's anchor. After it lands, re-pull the thread (so
  // each applied card hydrates as 'applied') and refresh the deck + preview.
  // Returns the {applied, failed} summary so the bar can flag anything skipped.
  const handleApplyAll: NonNullable<CommandBarProps['onApplyAll']> = async (cards) => {
    const res = await api.post<DeckApplyAllPayload>(`/api/chat/${slug}/_deck/apply-all`, { tools: cards });
    await chat.reloadThread();
    ed.refreshSummary();
    ed.bumpPreviewReload();
    notifyActivityChanged();
    const created = (res.applied || []).filter((a) => a.tool === 'create_slide' && a.slideId);
    const last = created[created.length - 1];
    if (last?.slideId) ed.setPendingNewSlideId(String(last.slideId));
    return res;
  };

  // Whole-deck "Reject all": dismiss the turn's pending deck cards in one pass
  // (server restores any previews owned by these cards and marks them rejected).
  // Re-pull the thread and reload the preview so restored slides are visible.
  const handleRejectAll: NonNullable<CommandBarProps['onRejectAll']> = async (cards) => {
    const res = await api.post(`/api/chat/${slug}/_deck/reject-all`, { tools: cards });
    await chat.reloadThread();
    notifyActivityChanged();
    // Discarding a card can take a slide back out of the deck (a new slide that
    // was sitting there for review), so the filmstrip has to re-read.
    ed.refreshSummary();
    ed.bumpPreviewReload();
    return res;
  };

  // Active-edits tray: jump to a running/ready turn. A slide turn focuses its
  // slide (and re-engages slide scope); a deck turn switches to whole-deck
  // scope. Either way open the conversation so a ready proposal is visible.
  function handleJumpToTurn(turn: JumpTarget) {
    setConvOpen(true);
    setSelectedIds(new Set()); // a live selection would otherwise shadow the target scope
    if (turn.kind === 'deck' || turn.kind === 'selection') {
      setScopeCleared(true);
      // Deck-scope rows still name a slide (a selection turn's cards do) —
      // clicking one must SHOW that slide, not just open the deck thread.
      if (turn.slideId) ed.jumpToSlide(turn.slideId);
    } else if (turn.slideId) {
      setScopeCleared(false);
      ed.jumpToSlide(turn.slideId);
    }
  }

  // The chooser's generative path. It used to close the gallery and leave half
  // a sentence in the composer, which read as being thrown out of the task; it
  // now opens a small modal OVER the gallery, so Cancel goes back to the tiles.
  function describeSlideInChat() {
    setDescribeSlide(addChooser || {});
  }

  /** The positional half of the ask, which the + already settled. The system
   *  prompt maps "directly after slide N" onto create_slide's
   *  insert_after_slide_id, so the wording is a contract, not prose. */
  function describePositionSentence(sectionKey?: string | null, refSlide?: SlideEntry | null): string {
    if (refSlide)
      return `Add a slide to the ${sectionName(refSlide.section)} section, directly after slide ${refSlide.idx + 1} ("${refSlide.title}"): `;
    if (sectionKey) return `Add a slide to the ${sectionName(sectionKey)} section: `;
    return 'Add a slide to the deck: ';
  }

  /** What the modal SHOWS about where the slide lands — the same fact as the
   *  sentence above, said to a person rather than to the model. */
  function describeWhere(sectionKey?: string | null, refSlide?: SlideEntry | null): string {
    if (refSlide) return `In ${sectionName(refSlide.section)}, after slide ${refSlide.idx + 1} — “${refSlide.title}”.`;
    if (sectionKey) return `At the end of ${sectionName(sectionKey)}.`;
    return 'At the end of the deck.';
  }

  // Hands the finished ask to the EXISTING autoSend path rather than starting a
  // turn here: one run loop in the editor, and this inherits its gates for
  // free. `deck: true` waits for deck scope the way Merge and Split do, and
  // `seedIfHeld` puts the ask in the composer when the bar cannot send now (a
  // streaming turn, an unreviewed proposal) instead of dropping or stacking it.
  // No `model` — an ordinary content turn follows the chat pill.
  function submitDescribeSlide(description: string) {
    const { sectionKey, refSlide } = describeSlide || {};
    setDescribeSlide(null);
    setAddChooser(null);
    setSelectedIds(new Set()); // a live selection would shadow deck scope and block autoSend
    setScopeCleared(true); // deck-scope ask
    // Not on a phone: the panel would cover the slide being written, and the
    // collapsed bar already announces the turn.
    if (!isPhone) setConvOpen(true);
    setAutoSend({
      prompt: describePositionSentence(sectionKey, refSlide) + description,
      deck: true,
      seedIfHeld: true,
    });
  }

  // The assets room's two verbs, and they are different verbs (2026-08-03).
  //
  // "Add to slide" STARTS the change: the button says it places the picture, the
  // room closes behind it, and leaving a pre-written sentence in the box for the
  // user to press send on made it read as the same button as "Add to chat" with
  // different wording (Alex, 2026-08-03). `seedIfHeld` keeps the old behaviour as
  // the FALLBACK — a busy chat or an unreviewed proposal on this slide lands the
  // ask in the box instead of spending or stacking a turn.
  //
  // It re-aims the chat at the focused slide on the way past — clearing a
  // filmstrip selection and un-clearing the scope — because the message it writes
  // says "this slide" and deck scope would point that at the whole deck. Same
  // choreography as the geometry badge above, and the visible scope-chip change
  // is deliberate: the alternative is a dead button.
  //
  // No `model`: unlike merge and split (which delete and create slides on
  // STRUCTURAL_MODEL), this is an ordinary one-slide content edit, so it follows
  // the chat pill exactly as the review bar's Tighten to fit does.
  function addAssetsToSlide(assets: ResolvedAsset[]) {
    if (!activeSlide) return;
    setDrawer(null);
    setSelectedIds(new Set()); // autoSend waits on this slide's scope
    setScopeCleared(false); // slide scope
    setConvOpen(true);
    setAutoSend({ prompt: assetAsk(assets), slideId: activeSlide.id, seedIfHeld: true });
  }

  // "Add to chat" is a fragment, not a sentence: it joins whatever the user is
  // already writing, never sends, and the room stays open so several files can be
  // named in one hand-written instruction.
  function addAssetsToChat(assets: ResolvedAsset[]) {
    setConvOpen(true);
    setPrefill({ text: assetRefs(assets), mode: 'append' });
  }

  // A slide's proposed edit overflows the frame: flow the overflow onto a new
  // slide. create_slide is deck-only, so switch to deck scope (mirrors the
  // Edit-tab "Fix in chat" handoff) and fire a one-shot split turn. The deck
  // model trims the source slide and creates a follow-on with the remainder —
  // two Apply cards the user reviews. We embed the PROPOSED (un-applied) JSX as
  // the content to divide: it isn't on disk yet, so read_other_slide would miss
  // the newly-added text that caused the overflow.
  //
  // Both split entry points run on `structuralModel`, NOT the chat pill: a split
  // creates a slide and rewrites another, so half the user's words end up in a
  // file that didn't exist a moment ago. Unlike merge there is no picker — split
  // is one click, with no options bar to hang one on.
  function handleSplitSlide(proposedJsx: string, targetSlide: SlideEntry) {
    if (!proposedJsx || !targetSlide) return;
    setSelectedIds(new Set()); // autoSend waits on deck scope — a selection would block it
    setScopeCleared(true); // → deck scope
    setConvOpen(true);
    setAutoSend({ prompt: buildSplitPrompt(targetSlide, proposedJsx), deck: true, model: structuralModel });
  }

  // "Tighten to fit" from the ON-SLIDE review bar. Same correction the chat
  // card sends (one string, lib/actionNotes.js), fired as a slide-scope turn on
  // the reviewed slide so the fix comes back as its own proposal card. The
  // model is left unnamed on purpose: autoSend falls through to the chat pill,
  // which is what the card's own Tighten does, so an OpenCode proposal's fix
  // stays on OpenCode rather than silently escalating.
  function handleTightenFromBanner(detail: string | null) {
    const slideId = activeReviewRow?.slideId;
    if (!slideId) return;
    setSelectedIds(new Set()); // autoSend waits on this slide's scope
    setScopeCleared(false); // slide scope
    setConvOpen(true);
    setAutoSend({ prompt: overflowFixPrompt(detail), slideId });
  }

  // Manual split from a slide's ⋮ menu. The slide is on disk (current), so no
  // JSX to embed — the deck model reads it itself. Same deck-scoped handoff.
  function handleSplitFromMenu(slide: SlideEntry) {
    if (!slide) return;
    setSelectedIds(new Set()); // autoSend waits on deck scope — a selection would block it
    setScopeCleared(true); // → deck scope
    setConvOpen(true);
    setAutoSend({ prompt: buildSplitPrompt(slide, null), deck: true, model: structuralModel });
  }

  // Builds the deck-scoped split instruction. When `proposedJsx` is given (the
  // chat-card overflow case), embed it as the content to divide — it isn't on
  // disk yet, so the model must NOT read the slide. When omitted (the menu
  // case), the on-disk slide is current, so instruct the model to read it first.
  function buildSplitPrompt(targetSlide: SlideEntry, proposedJsx: string | null) {
    const title = targetSlide?.title ? `"${targetSlide.title}"` : 'this slide';
    // The transcript shows "↻ Split "Q3 results" into two slides", not this
    // numbered script: see lib/actionNotes.js.
    const note = (text: string) => actionPrompt('splitSlide', `${title} into two slides`, text);
    const section = targetSlide?.section ? sectionName(targetSlide.section) : 'its';
    const base =
      `The slide ${title} (id ${targetSlide?.id}) has more content than fits one slide. ` +
      `Split it into TWO slides, keeping every word: do not truncate, summarise, or invent anything.\n\n` +
      `1. Call edit_slide on ${targetSlide?.id} to keep roughly the first half of the content so it fits ` +
      `the frame, in the same primitive/layout and with the same title.\n` +
      `2. Call create_slide in the ${section} section with insert_after_slide_id set to ${targetSlide?.id}, ` +
      `holding the remaining content in the same layout. Give it the same title or a short continuation label.\n\n` +
      `Divide at a natural boundary (a section heading, a bullet, or a paragraph break) so neither slide overflows.`;
    if (proposedJsx) {
      return note(
        base +
          `\n\nHere is the full content to divide between the two slides (divide THIS; do not read the slide):\n\n` +
          '```jsx\n' +
          proposedJsx +
          '\n```',
      );
    }
    return note(
      base + `\n\nFirst call read_other_slide for ${targetSlide?.id} to get its current content, then split it.`,
    );
  }

  // ----- merge --------------------------------------------------------------

  // The picker confirmed: `slides` is already in deck order, so the first is
  // the one that survives.
  // The picker also carries the model it was showing (the merge bar's Model row,
  // defaulting to `structuralModel`) — a merge issues N deletes, so it must not
  // ride whatever the chat pill was left on.
  function handleMergeConfirm({ slides, level, instructions, model }: MergeRequest) {
    setOverview(null);
    if (!slides || slides.length < 2) return;
    setSelectedIds(new Set()); // autoSend waits on deck scope — a selection would block it
    setScopeCleared(true); // → deck scope (delete_slide is deck-only)
    setConvOpen(true);
    setAutoSend({
      prompt: buildMergePrompt(slides, level, instructions),
      deck: true,
      model: model || structuralModel,
    });
  }

  // Builds the deck-scoped merge instruction. Deliberately names the ids rather
  // than the slide numbers: numbers shift as the deck changes, ids don't. The
  // content level is passed as a bare token — what each level permits is spelt
  // out in the deck system prompt, not restated here.
  function buildMergePrompt(slides: SlideEntry[], level: string, instructions: string) {
    const [keep, ...remove] = slides;
    // The caller already refused fewer than two, so this is never the case.
    if (!keep) return '';
    const label = (s: SlideEntry) => `${s.id}${s.title ? ` ("${s.title}")` : ''}`;
    const base =
      `Merge these ${slides.length} slides into one, in this order:\n` +
      slides.map((s, i) => `${i + 1}. ${label(s)}`).join('\n') +
      `\n\nThe slide that SURVIVES is ${keep.id}: it keeps its position, section and accent.\n` +
      `1. Call read_other_slide for every one of the ${slides.length} slides above.\n` +
      `2. Call edit_slide on ${keep.id} holding the combined content of all of them, in the order listed.\n` +
      `3. Call delete_slide for each of the other slides: ${remove.map((s) => s.id).join(', ')}. ` +
      `Issue every one of those deletes: leaving a slide behind duplicates its content in the deck.\n\n` +
      `Content level: ${level}`;
    const extra = (instructions || '').trim();
    const body = extra ? `${base}\n\nAdditional instructions: ${extra}` : base;
    // The transcript shows "↻ Merged 3 slides into "Q3 results"" rather than the
    // numbered script: see lib/actionNotes.js.
    const detail = `${slides.length} slides${keep.title ? ` into "${keep.title}"` : ''}`;
    return actionPrompt('mergeSlides', detail, body);
  }

  // ----- change template ----------------------------------------------------

  // Open the template chooser for a slide (from its ⋮ menu).
  function handleChangeTemplate(slide: SlideEntry) {
    setChangeTemplate(slide);
  }

  // The chooser confirmed a target template: fire a slide-scope AI turn that
  // re-flows the slide's existing content into it. No deterministic path exists
  // (templates have different prop shapes), so this rides the edit_slide chat
  // flow — read_template then rewrite, per slide-edit.system.md — surfacing as a
  // normal preview → apply card. autoSend defers the actual send until the scope
  // has caught up to this slide (CommandBar guards on slideId), so it never
  // fires on a stale scope.
  //
  // CONFIRMING HANDS THE WORK OFF (Jake, 2026-08-01). The chooser used to stay
  // open for the whole re-flow, holding the user on a modal for a minute or two
  // with the slide they'd just changed hidden behind it. It now closes the
  // moment the turn is fired and the run continues in the background like every
  // other request: the user lands back on the slide, and the Activity tray, the
  // filmstrip dot, the toasts below and the ProposalBanner report the outcome
  // (Activity is also where a background run is stopped). The modal only stays
  // open for a PRE-FLIGHT refusal, which happens before anything is running.
  function handleChangeTemplateConfirm({ pathTail, title, variant, model }: TemplateChoice) {
    const slide = changeTemplate;
    if (!slide) return;
    const key = slideScope(slug, slide).key;
    const detail = `«${title}»${variant ? ` · ${variant}` : ''}`;
    // The store refuses a second turn on a scope that's already streaming, and
    // it refuses SILENTLY: so ask first, or the chooser waits on a turn that
    // will never start.
    if (getSlice(key).status.kind === 'streaming') {
      setTemplateRun({
        key,
        slideId: slide.id,
        detail,
        phase: 'failed',
        message: 'The AI is already working on this slide. Wait for that to finish, or stop it from the Activity tab.',
      });
      return;
    }
    const importPath = `@template/${pathTail}`;
    // Naming the version matters as much as naming the template: read_template
    // seeds the wrapper from a variant, so without this the model starts from the
    // template's FIRST version and the user's pick is silently lost.
    const target = variant
      ? `the «${variant}» version of the «${title}» template (${importPath})`
      : `the «${title}» template (${importPath})`;
    const readCall = variant
      ? `First call read_template for ${importPath} with variant "${variant}": the wrapper it returns IS that` +
        ` version, so keep every layout prop it sets (layout, align, columns, mono, scrim, side…) and replace only` +
        ` the content props.`
      : `First call read_template for ${importPath}.`;
    // The whole instruction, with no user free-text tail: the chooser's optional
    // instructions box is gone (it read as an unjudgeable "tell the AI more"
    // field). Anything extra is a follow-up message in the chat.
    const body =
      `Change this slide to ${target}, keeping ALL of its existing content:` +
      ` the same headline, body, stats, people and imagery, just re-flowed into that template.` +
      ` ${readCall} Then use edit_slide to rewrite this slide so it imports` +
      ` the template and renders it as the whole slide, moving the current content into the template's props only.` +
      ` Do not invent new content and do not drop anything.` +
      // Unconditional, and separate from the copy lock: "keep all the content"
      // protects the ELEMENTS, and a model re-flowing into a tighter template
      // will happily rephrase while honouring it. A template swap is never a
      // licence to rewrite, whether or not the author pinned the slide.
      ` Reproduce every word of the existing content exactly, same wording and punctuation:` +
      ` you are moving text into new props, not rewriting it. If it does not fit the new template,` +
      ` say so rather than trimming the words.`;
    setTemplateRun({ key, slideId: slide.id, detail, phase: 'starting', startedAt: Date.now() });
    setChangeTemplate(null); // back to the slide; the turn runs in the background
    ed.toast.pushToast(`Re-flowing this slide into ${detail}. It will appear here when it is ready.`);
    setSelectedIds(new Set()); // autoSend waits on this slide's scope — a selection would block it
    setScopeCleared(false); // slide scope
    ed.jumpToSlide(slide.id); // focus the target slide
    setConvOpen(true); // open the conversation
    setAutoSend({
      prompt: actionPrompt('changeTemplate', detail, body),
      slideId: slide.id,
      // The chooser's own Model row, which starts from the ⚙ default. One slide
      // re-flowed in place, so no reason to reach past the user's setting the
      // way merge and split do.
      model: model || defaultChoice,
    });
  }

  // Watch the fired turn WITHOUT opening its scope (that would clear the unread
  // dot and swallow the finished toast for a turn nobody read). The modal is
  // already gone by now, so every exit reports as a toast on the slide — and
  // every exit IS accounted for, because a background run that ends in silence
  // is worse than no background run at all.
  const templateSlice = useTurnSlice(templateRun?.key || null);
  useEffect(() => {
    if (!templateRun) return;
    const { phase, startedAt = 0 } = templateRun;
    if (phase === 'failed') return; // a pre-flight refusal: the chooser is showing it
    const streaming = templateSlice.status.kind === 'streaming';

    // Success can be observed from ANY phase: a turn that starts and finishes
    // between two renders would otherwise strand this watcher in 'starting'.
    // runTurn re-pulls the persisted thread before it goes idle, so the card is
    // in `messages` by the time we see the transition.
    const landed = templateSlice.messages.some(
      (m) =>
        m.role === 'assistant' &&
        (m.ts || 0) >= startedAt - 1000 &&
        Array.isArray(m.content) &&
        m.content.some((b) => b.type === 'tool_use' && SLIDE_TOOL_NAMES.has(b.name)),
    );
    if (landed && !streaming) {
      const { slideId, detail } = templateRun;
      setTemplateRun(null);
      // Same rule as a landing proposal: say it, offer it, never navigate.
      // This ran a minute or two in the background, by which time "this slide"
      // may not be the slide on screen — and the re-flow was asked for from a
      // modal that already closed, so a jump arriving now is a surprise.
      const where = slideLabelFor(slideId);
      ed.toast.pushToast(`${where ? `${where[0]?.toUpperCase()}${where.slice(1)}` : 'That slide'} is now ${detail}.`, {
        actionLabel: 'Review',
        undo: async () => ed.jumpToSlide(slideId),
      });
      return;
    }
    if (streaming) {
      if (phase !== 'running') setTemplateRun((r) => (r ? { ...r, phase: 'running' } : r));
      return;
    }
    if (templateSlice.status.kind === 'error') {
      // The store's message is already written for the user (in-flight cap, a
      // clashing slide turn, a lost connection, a server turn error).
      setTemplateRun(null);
      ed.toast.pushToast(templateSlice.status.message);
      return;
    }
    if (phase === 'running') {
      // Finished, no card. Stopping it from the Activity tab is the ordinary way
      // that happens, so this stays neutral about whose decision it was.
      setTemplateRun(null);
      ed.toast.pushToast('That slide was not changed. The chat holds the reply.');
    }
  }, [templateRun, templateSlice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Last guard: a send that never reaches the store at all (the refusal paths
  // are handled, but this cannot be allowed to depend on enumerating them).
  useEffect(() => {
    if (templateRun?.phase !== 'starting') return;
    const t = setTimeout(() => {
      // Any change of phase (including clearing the run) re-runs this effect and
      // cancels the timer, so reaching here means it is still stuck in 'starting'.
      setTemplateRun(null);
      ed.toast.pushToast('The template change never started. Check the Activity tab, then try again.');
    }, 20000);
    return () => clearTimeout(t);
  }, [templateRun?.phase, templateRun?.startedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----- save to library ----------------------------------------------------

  // Copy slides (snapshots, assets included) into a library shelf. The bank
  // decks are ordinary decks the server creates (pipeline/banks.ts) — saving
  // FROM a bank is meaningless, so those slugs don't get the menu item.
  // Opens the category-pick modal; the actual save happens below. Both entry
  // points land here: the filmstrip ⋮ (one slide) and the overview's select
  // mode (a run of them, kept as one item).
  function handleSaveToBank(slide: SlideEntry) {
    setSaveToLibrary([slide]);
  }

  async function handleSaveToLibraryConfirm(target: SaveToLibraryPayload) {
    const picked = saveToLibrary;
    setSaveToLibrary(null);
    if (!picked?.length) return;
    await mutate.run(() => api.post<LibrarySavePayload>('/api/library/save', { fromSlug: slug, ...target }), {
      toast: (r) =>
        r.count === 1 ? `Saved "${r.title}" to the RKH Library` : `Saved ${r.count} slides to the RKH Library`,
      undo: async (r) => {
        // A save that created a fresh item removes the whole item (its deck
        // goes to Trash); appending to an existing item removes just the
        // slides it added.
        if (r.createdItem) {
          await api.delete(`/api/library/items/${r.slug}`);
        } else {
          for (const id of [...(r.slideIds || [])].reverse()) {
            await api.delete(`/api/slides/${r.slug}/slide?id=${encodeURIComponent(id)}`);
          }
        }
      },
    });
  }

  // A selection-scope send fired: the turn holds its own captured slide list,
  // so drop the UI selection (the streaming dots take over) and stay on deck
  // scope — the same thread the selection turn streams into.
  function handleSelectionSent() {
    setSelectedIds(new Set());
    setScopeCleared(true);
  }

  // "Edit with AI" (the floating selection bar): the explicit opt-in that
  // turns the selection into the chat scope, then opens the conversation on
  // the Chat tab with the input focused — the click must visibly land the
  // user somewhere they can type.
  function handleEditSelection() {
    setSelectionArmed(true);
    setConvOpen(true);
    setForceChatTab(true);
    setTimeout(() => inputRef.current?.focus(), 180);
  }

  // Slides (not blocks) covered by the current selection, for the bar label.
  const selectedCount = useMemo(() => {
    if (!selectedIds.size) return 0;
    return (slides || []).reduce((n, s) => n + (selectedIds.has(baseOf(s.id)) ? 1 : 0), 0);
  }, [selectedIds, slides]);

  const chatEmpty =
    scope.kind === 'selection' ? (
      <div className="conversation__empty">
        <p>
          You're talking to the <strong>{scope.label} you selected</strong>: one instruction, applied to every one of
          them by a single agent.
        </p>
        <p className="muted small">
          Try: <em>"give the typography more breathing room"</em>, <em>"tighten every title to under 8 words"</em>.
        </p>
        <p className="muted small">
          Each slide comes back as its own proposal: <strong>Keep change</strong> or <strong>Discard change</strong>{' '}
          them one by one, or use Apply all.
        </p>
      </div>
    ) : scope.kind === 'slide' ? (
      <div className="conversation__empty">
        <p>
          Ask for changes to <strong>{scope.label}</strong>: the studio sees the slide JSX, the brand rules, and the
          primitive catalogue.
        </p>
        <p className="muted small">
          Proposals show as cards: <strong>Preview</strong> them live in the deck, then <strong>Keep change</strong> or{' '}
          <strong>Discard change</strong>. Nothing changes until you keep it.
        </p>
        <p className="muted small">
          The chip below says what your message will change. Switch it to <strong>whole deck</strong> to add, remove or
          reorder slides instead.
        </p>
      </div>
    ) : (
      <div className="conversation__empty">
        <p>
          You're talking to the <strong>whole deck</strong>: add or rework slides, restructure sections, or audit the
          flow.
        </p>
        <p className="muted small">
          Try: <em>"add a slide about pricing to the closing section"</em>,{' '}
          <em>"audit the deck for titles over 8 words"</em>.
        </p>
        <p className="muted small">
          Reordering, deleting and renaming don't need the AI: drag tiles in the filmstrip, or use a tile's ⋮ menu.
        </p>
      </div>
    );

  return (
    <div className="editor">
      {/* The editor's bar is the same component as every browsing surface,
          with three slots answered differently: the emblem is dropped (this
          is a workspace, not a front door), the wordmark is replaced by the
          deck's NAME — the slug moves to the tooltip, since that is what the
          URL and the filesystem call it — and Publish is pinned so the one
          irreversible action never hides behind a menu. */}
      <AppHeader
        compact
        hideMark
        title={summary?.title || slug}
        titleHint={slug}
        meta={ed.status === 'ready' ? `${slides.length} slides` : null}
        showNav={false}
        lead={
          <button
            type="button"
            className="iconbtn"
            onClick={() => navigate('/')}
            title="Back to decks"
            aria-label="Back to decks"
          >
            <ChevronLeft />
          </button>
        }
        actions={[
          { key: 'builds', label: 'Builds', render: () => <BuildsIndicator /> },
          {
            key: 'theme',
            label: 'Appearance',
            ownRow: true,
            render: ({ inSheet }) => <ThemeToggle inSheet={inSheet} />,
          },
          {
            key: 'settings',
            label: 'Settings',
            barLabel: <Settings />,
            barClassName: 'iconbtn',
            hint: 'Models, engines, storage',
            onSelect: () => setDrawer('settings'),
          },
          {
            key: 'workbench',
            label: 'Workbench',
            href: '/workbench/',
            external: true,
            barClassName: 'btn btn--ghost btn--sm canvas__catalogue',
            hint: 'Browse every primitive, template and shared slide, then ask chat to use one by name',
          },
          {
            key: 'options',
            label: 'Options',
            hint: 'Deck-wide display options (section label, more to come)',
            onSelect: () => setDrawer('options'),
          },
          { key: 'assets', label: 'Assets', onSelect: () => setDrawer('assets') },
        ]}
        pinned={[
          {
            key: 'publish',
            label: 'Publish',
            primary: true,
            barClassName: 'btn btn--primary btn--sm',
            onSelect: () => setDrawer('publish'),
          },
        ]}
      />

      {ed.status === 'error' && (
        <div className="editor__error">
          <strong>Could not open deck</strong>
          <p className="muted">{ed.error}</p>
          <Link to="/" className="link">
            Back to decks
          </Link>
        </div>
      )}

      {ed.status !== 'error' && (
        <div className="editor__stage">
          <Filmstrip
            slug={slug}
            sections={summary?.sections || []}
            slides={slides}
            activeIdx={activeIdx}
            activity={slideActivity}
            pending={pendingSlideIds}
            review={reviewSlideIds}
            blocked={blockedSlideIds}
            selected={selectedIds}
            onSelectedChange={setSelectedIds}
            onSelect={ed.selectSlide}
            onShowOverview={ed.status === 'ready' ? () => setOverview({ mode: null }) : null}
            onAddSlide={ed.handleAddSlide}
            onReorderCommit={ed.commitReorder}
            onReorderMultiCommit={ed.commitMultiReorder}
            onDuplicate={ed.handleDuplicate}
            onToggleHidden={ed.handleToggleHidden}
            onRenameSlide={ed.handleRenameSlide}
            onMoveToSection={ed.handleMoveToSection}
            onChangeTemplate={handleChangeTemplate}
            onSaveToBank={BANK_SLUGS.has(slug) ? null : handleSaveToBank}
            onDeleteSlide={ed.handleDeleteSlide}
            onRenameSection={ed.handleRenameSection}
            onRecolourSection={ed.handleRecolourSection}
            onDeleteSection={ed.handleDeleteSection}
            onCreateSection={ed.handleCreateSection}
            onAddSection={ed.status === 'ready' ? sectionBuild.open : null}
            onMoveSection={ed.handleMoveSection}
            onReorderSections={ed.handleReorderSections}
            busy={mutate.busy}
            generating={sectionBuild.phase === 'building'}
          />
          {ed.status === 'ready' ? (
            <PreviewFrame
              slug={slug}
              activeIdx={activeIdx}
              onActiveChange={ed.handlePreviewActiveChange}
              reloadKey={ed.previewReloadKey}
              canFix
            />
          ) : (
            <main className="preview-frame">
              <div className="preview-frame__skeleton">
                <span>Opening deck…</span>
              </div>
            </main>
          )}
          {activeReviewRow && activeSlideWorking && (
            <div className="proposal-banner proposal-banner--working" role="status" aria-label="AI edit in progress">
              <span className="proposal-banner__dot" aria-hidden="true" />
              <span className="proposal-banner__label">AI edit</span>
              <span className="muted small">Studio is still adjusting this slide — review opens when it settles.</span>
            </div>
          )}
          {activeReviewRow && !activeSlideWorking && (
            <ProposalBanner
              slug={slug}
              row={activeReviewRow}
              queue={reviewQueue}
              onJumpToSlide={ed.jumpToSlide}
              onChanged={handleActivityChanged}
              onTighten={handleTightenFromBanner}
            />
          )}
          {!activeReviewRow && selectedCount > 0 && !selectionArmed && (
            <div className="proposal-banner proposal-banner--selection" role="status">
              <span className="proposal-banner__label">
                {selectedCount} slide{selectedCount === 1 ? '' : 's'} selected
              </span>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={handleEditSelection}
                title="One instruction, applied to every selected slide: each comes back as its own proposal"
              >
                Edit with AI
              </button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSelectedIds(new Set())}>
                Clear
              </button>
            </div>
          )}
          {!activeReviewRow && selectedCount === 0 && reviewQueue.length > 0 && (
            <div className="proposal-banner proposal-banner--queue" role="status">
              <span className="proposal-banner__dot" aria-hidden="true" />
              <span className="proposal-banner__label">
                {reviewQueue.length} proposal{reviewQueue.length === 1 ? '' : 's'} awaiting review
              </span>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                // Releasing first matters when the queued proposal is on the
                // slide being edited: the jump is a no-op there, so without
                // this the hold stays on and Review looks like a dead click.
                onClick={() => {
                  releaseBusy();
                  const first = reviewQueue[0]?.slideId;
                  if (first) ed.jumpToSlide(first);
                }}
                title="Walk the deck proposal by proposal: keep or discard each, and it steps you to the next"
              >
                Review
              </button>
            </div>
          )}
          <CommandBar
            scope={scope}
            chat={chat}
            open={convOpen}
            onToggleOpen={setConvOpen}
            onClearScope={
              scope.kind === 'slide'
                ? () => setScopeCleared(true)
                : scope.kind === 'selection'
                  ? () => {
                      setSelectedIds(new Set());
                      setScopeCleared(true); // the chip's alternative reads "Whole deck"
                    }
                  : null
            }
            onSelectSlideScope={
              activeSlide
                ? () => {
                    setSelectedIds(new Set());
                    setScopeCleared(false);
                  }
                : null
            }
            onSelectionSent={handleSelectionSent}
            slides={slides}
            onJumpToSlide={ed.jumpToSlide}
            onApplied={handleApplied}
            onApplyAll={scope.kind !== 'slide' ? handleApplyAll : null}
            onRejectAll={scope.kind !== 'slide' ? handleRejectAll : null}
            empty={chatEmpty}
            inputRef={inputRef}
            disabled={ed.status !== 'ready'}
            prefill={prefill}
            onPrefillConsumed={() => setPrefill(null)}
            autoSend={autoSend}
            // `dispatched` is false when the command bar refused the send (busy,
            // or the editor isn't ready). Anything waiting on that turn has to
            // hear about it rather than sit in 'starting' until the watchdog.
            onAutoSendConsumed={(dispatched) => {
              setAutoSend(null);
              if (dispatched === false) {
                setTemplateRun((r) =>
                  r?.phase === 'starting'
                    ? { ...r, phase: 'failed', message: "Studio couldn't start the turn just now. Try again." }
                    : r,
                );
              }
            }}
            onSplitSlide={handleSplitSlide}
            sections={summary?.sections || []}
            onSlideEdited={ed.bumpPreviewReload}
            onToggleHidden={ed.handleToggleHidden}
            onToggleLayoutOk={ed.handleToggleLayoutOk}
            onToggleCopyLocked={ed.handleToggleCopyLocked}
            onToggleSectionChrome={ed.handleToggleSectionChrome}
            deckOptions={ed.summary?.options}
            forceTab={ed.forceEditTab ? 'edit' : forceChatTab ? 'chat' : forceActivityTab ? 'activity' : null}
            onForceTabConsumed={() => {
              ed.clearForceEditTab();
              setForceChatTab(false);
              setForceActivityTab(false);
            }}
            activity={activity}
            onJumpToTurn={handleJumpToTurn}
            onActivityChanged={handleActivityChanged}
            onStopRun={handleStopRun}
            findings={findings}
            onActivityOpened={() => setWarmFindings(true)}
          />
          {overview && ed.status === 'ready' && (
            <DeckOverview
              slug={slug}
              slides={slides}
              sections={summary?.sections || []}
              activeIdx={activeIdx}
              onSelect={(idx) => {
                ed.selectSlide(idx);
                setScopeCleared(false);
              }}
              onClose={() => setOverview(null)}
              onReorder={ed.commitReorder}
              onReorderMulti={ed.commitMultiReorder}
              onSplitSlide={(slide) => {
                setOverview(null);
                handleSplitFromMenu(slide);
              }}
              onDeleteSlides={ed.handleDeleteSlides}
              onMergeSlides={handleMergeConfirm}
              modelOptions={modelOptions}
              onSaveSlides={
                BANK_SLUGS.has(slug)
                  ? null
                  : (picked) => {
                      setOverview(null);
                      setSaveToLibrary(picked);
                    }
              }
              onInsertSlides={() => {
                setOverview(null);
                const ref = activeSlide || slides[slides.length - 1] || null;
                ed.handleAddSlide(null, ref);
              }}
              onRecolourSection={ed.handleRecolourSection}
              onRenameSection={ed.handleRenameSection}
              initialMode={overview.mode}
              seedSlideId={overview.seedSlideId}
              busy={mutate.busy}
            />
          )}
        </div>
      )}

      <Toast toast={ed.toast.toast} onUndo={ed.toast.runUndo} onDismiss={ed.toast.dismissToast} />

      {addChooser && (
        <AddSlideChooser
          where={
            addChooser.refSlide
              ? `after "${addChooser.refSlide.title}"`
              : addChooser.sectionKey
                ? `to ${sectionName(addChooser.sectionKey)}`
                : ''
          }
          deckSlug={slug}
          onDescribe={describeSlideInChat}
          describeOpen={!!describeSlide}
          onPickTemplate={ed.handleAddFromTemplate}
          onPickBatch={ed.handleAddFromTemplateBatch}
          onPickSection={ed.handleAddSectionFromLibrary}
          onUploadSlide={ed.handleUploadSlide}
          onClose={() => setAddChooser(null)}
          busy={mutate.busy}
        />
      )}

      {/* Over the chooser, not instead of it, so Cancel goes back to the
          gallery rather than out of the task entirely. */}
      {describeSlide && (
        <DescribeSlideModal
          where={describeWhere(describeSlide.sectionKey, describeSlide.refSlide)}
          onCancel={() => setDescribeSlide(null)}
          onSubmit={submitDescribeSlide}
          busy={mutate.busy}
        />
      )}

      {sectionBuild.isOpen &&
        (sectionBuild.phase === 'intake' || sectionBuild.phase === 'planning' || sectionBuild.phase === 'review') && (
          <SectionModal
            sections={summary?.sections || []}
            stockOk
            busy={mutate.busy}
            planPhase={sectionBuild.phase === 'planning' || sectionBuild.phase === 'review' ? sectionBuild.phase : null}
            plan={sectionBuild.plan}
            planBusy={sectionBuild.planBusy}
            planErrors={sectionBuild.planErrors}
            onCreateEmpty={async (args) => {
              await ed.handleCreateSection(args);
              sectionBuild.close();
            }}
            onBuild={sectionBuild.startBuild}
            onApprovePlan={sectionBuild.approvePlan}
            onCancel={sectionBuild.cancel}
            onCancelReview={sectionBuild.cancel}
          />
        )}

      {changeTemplate && (
        <ChangeTemplateChooser
          slide={changeTemplate}
          onConfirm={handleChangeTemplateConfirm}
          // Confirming closes this modal itself, so by the time anything is
          // running the chooser is gone. Closing therefore only ever abandons a
          // pick, never work.
          onClose={() => {
            setChangeTemplate(null);
            setTemplateRun(null);
          }}
          busy={mutate.busy}
          run={templateRun}
          modelOptions={modelOptions}
          defaultModel={defaultChoice}
          onOpenChat={() => {
            setChangeTemplate(null);
            setTemplateRun(null);
            setConvOpen(true);
            setForceChatTab(true);
          }}
        />
      )}

      {saveToLibrary && (
        <SaveToLibraryModal
          slug={slug}
          slides={saveToLibrary}
          sections={summary?.sections || []}
          deckTitle={summary?.title || slug}
          busy={mutate.busy}
          onCancel={() => setSaveToLibrary(null)}
          onSave={handleSaveToLibraryConfirm}
        />
      )}

      {drawer === 'options' && (
        <DeckOptionsDrawer slug={slug} onClose={() => setDrawer(null)} onChanged={ed.bumpPreviewReload} />
      )}
      {drawer === 'assets' && (
        <AssetsModal
          slug={slug}
          activeSlide={activeSlide}
          chatBusy={chat.streaming !== null}
          onAddToSlide={addAssetsToSlide}
          onAddToChat={addAssetsToChat}
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer === 'publish' && (
        <PublishDrawer
          slug={slug}
          onClose={() => setDrawer(null)}
          onJumpToSlide={ed.jumpToSlide}
          // The preview's reload counter doubles as "a slide changed": every
          // mutation path bumps it, so validation re-reads on the same signal
          // the preview refreshes on.
          revision={ed.previewReloadKey}
        />
      )}
      {drawer === 'settings' && <SettingsDrawer onClose={() => setDrawer(null)} />}
    </div>
  );
}
